// API utility functions for EliteHome frontend
// Use proxy in dev (same origin) so /api/* goes to backend; set REACT_APP_API_URL in production if backend is elsewhere
const API_BASE_URL = process.env.REACT_APP_API_URL !== undefined && process.env.REACT_APP_API_URL !== ""
    ? process.env.REACT_APP_API_URL
    : "";

/**
 * Get authentication headers with JWT token
 */
export const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` })
    };
};

/**
 * Make authenticated API request
 */
export const apiRequest = async (endpoint, options = {}) => {
    const url = `${API_BASE_URL}${endpoint}`;
    const config = {
        ...options,
        headers: {
            ...getAuthHeaders(),
            ...options.headers
        }
    };

    try {
        console.log(`Making API request to: ${url}`);

        const response = await fetch(url, config);

        // Check if response is HTML (error page)
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('text/html')) {
            const htmlText = await response.text();
            console.error(`Server returned HTML instead of JSON for ${endpoint}:`, htmlText.substring(0, 200));
            throw new Error(`Server error: Received HTML response instead of JSON`);
        }

        const data = await response.json();

        if (!response.ok) {
            const message =
                data?.message ||
                data?.error ||
                JSON.stringify(data) ||
                `HTTP error! status: ${response.status}`;
            console.error("API request failed:", {
                status: response.status,
                message,
                error: data?.error
            });
            const err = new Error(message);
            err.status = response.status;
            err.data = data;
            throw err;
        }

        return data;
    } catch (error) {
        if (!error.status) {
            console.error(`API request failed for ${endpoint}:`, {
                message: error.message
            });
        }
        throw error;
    }
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = () => {
    const token = localStorage.getItem("token");
    const user = localStorage.getItem("user");
    return !!(token && user);
};

/**
 * Get current user info
 */
export const getCurrentUser = () => {
    const userStr = localStorage.getItem("user");
    const role = localStorage.getItem("role");

    if (!userStr) return null;

    try {
        const user = JSON.parse(userStr);
        return { ...user, role };
    } catch {
        return null;
    }
};

/**
 * Check if user has specific role
 */
export const hasRole = (role) => {
    const currentRole = localStorage.getItem("role");
    return currentRole === role;
};

/**
 * Check if user may access Safe (vault)
 * - admin: always allowed
 * - staff: only if can_use_safe flag is true
 */
export const hasSafeAccess = () => {
    const user = getCurrentUser();
    if (!user) return false;
    if (user.role === "admin") return true;
    if (user.role === "staff" && user.can_use_safe) return true;
    return false;
};

/**
 * Logout user
 */
export const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("role");
};

/**
 * API endpoints
 */
export const API_ENDPOINTS = {
    // Auth
    LOGIN: "/api/auth/login",
    REGISTER: "/api/auth/register",
    CHECK_TOKEN: "/api/auth/check",

    // Admin
    ADMIN_USERS: "/api/admin/users",
    ADMIN_STATS: "/api/admin/stats",
    ADMIN_NOTIFICATIONS: "/api/admin/notifications",
    ADMIN_INVOICES: "/api/admin/invoices",
    ADMIN_FINANCES: "/api/admin/finances",
    ADMIN_OWNERS: "/api/admin/owners",
    ADMIN_EMPLOYEES: "/api/admin/employees",
    ADMIN_SALARIES: "/api/admin/salaries",
    ADMIN_SPECIAL_PAYMENTS: "/api/admin/special-payments",

    // Owner
    OWNER_DASHBOARD: "/api/owner/dashboard",
    OWNER_APARTMENTS: "/api/owner/apartments",
    OWNER_INVOICES: "/api/owner/invoices",
    OWNER_FINANCIALS: "/api/owner/financials",
    OWNER_SPECIAL_PAYMENTS: "/api/owner/special-payments",

    // Public
    APARTMENTS: "/api/apartments",
    /** GET: distinct owner usernames (auth) — for apartment forms */
    APARTMENT_OWNERS_LIST: "/api/apartments/owners-list",
    /** GET: financial records for one apartment (auth) */
    APARTMENT_FINANCIALS: (id) => `/api/apartments/${id}/financial`,
    NOTIFICATIONS: "/api/notifications",

    // Protected
    INVOICES: "/api/invoices",
    FINANCES: "/api/finances",

    // Safe (vault) - admin & staff only
    SAFE: "/api/safe"
};
